import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { HexColorPicker } from "react-colorful";

import { ClickAwayListener, makeStyles } from "@material-ui/core";
import {
	FormatBold as BoldIcon,
	FormatItalic as ItalicIcon,
	FormatUnderlined as UnderlineIcon,
	FormatStrikethrough as StrikeIcon,
	FormatClear as ClearIcon,
	FormatListBulleted as BulletListIcon,
	FormatListNumbered as NumberedListIcon,
	Undo as UndoIcon,
	Redo as RedoIcon,
	ExpandMore as ExpandIcon,
	ExpandLess as CollapseIcon,
	Title as HeadingIcon,
} from "@material-ui/icons";
import { Button, Dropdown, Popover, Overlay } from "react-bootstrap";

const useStyles = makeStyles({
	root: {
		display: "flex",
		marginBottom: 6,
		height: 20,
	},
	btnsContainer: {
		display: "flex",
		alignItems: "center",

		"&:not(:first-of-type)": {
			marginLeft: 4,
		},
		"& > button:not(:first-child)": {
			marginLeft: 4,
		},
	},
	primaryBtnsContainer: {
		marginRight: "auto",
	},
	textBtn: {
		height: 20,
		padding: "0px 4px 0px 2px",
		lineHeight: "14px",
		display: "block",
		"&:after": {
			verticalAlign: "0.155em",
		},
		"& svg": {
			width: 14,
			height: 14,
			fontSize: 12,
		},
	},
	colorBtn: {
		padding: 4,
		width: 20,
		height: 20,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginLeft: 4,
		"& > span": {
			width: "100%",
			height: "100%",
			display: "block",
			borderRadius: 2,
		},
	},
	toolBtn: {
		padding: 0,
		width: 20,
		height: 20,
		lineHeight: "18px",
		"& svg": {
			width: 14,
			height: 14,
			fontSize: 12,
		},
	},
	activeBtn: {
		fontWeight: "bold",
	},
	actionBtn: {
		height: 20,
		padding: "0px 4px",
		fontSize: 12,
		whiteSpace: "pre",
	},
});

const Toolbar = (props) => {
	const { editor, actions, expanded, setExpanded, disableBtns, headingLevel, color, setColor, extensions } = props;
	const classes = useStyles(props);
	const [colorPickerOpen, setColorPickerOpen] = useState(false);
	const target = React.createRef();

	if (!editor) {
		return null;
	}

	const focused = editor.isFocused;
	const disabled = disableBtns || !expanded;

	return (
		<div className={classes.root}>
			<div className={clsx(classes.btnsContainer, classes.primaryBtnsContainer)}>
				{extensions.heading && (
					<Dropdown>
						<Dropdown.Toggle size="sm" variant="outline-secondary" className={classes.textBtn} disabled={disabled}>
							<HeadingIcon />
						</Dropdown.Toggle>

						<Dropdown.Menu>
							{[1, 2, 3, 4, 5, 6].map((level) => (
								<Dropdown.Item
									key={level}
									onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
									active={headingLevel === level}
								>
									Heading {level}
								</Dropdown.Item>
							))}
						</Dropdown.Menu>
					</Dropdown>
				)}

				{extensions.color && (
					<div ref={target}>
						<Button
							variant="outline-secondary"
							className={classes.colorBtn}
							onClick={() => setColorPickerOpen(!colorPickerOpen)}
							disabled={disabled}
						>
							<span style={{ backgroundColor: color }}></span>
						</Button>
						<Overlay target={target} show={colorPickerOpen} placement="bottom" container={target.current}>
							<Popover>
								<ClickAwayListener onClickAway={() => setColorPickerOpen(false)}>
									<Popover.Content>
										<HexColorPicker color={color} onChange={setColor} />
									</Popover.Content>
								</ClickAwayListener>
							</Popover>
						</Overlay>
					</div>
				)}

				{extensions.bold && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("bold") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleBold().run()}
						className={clsx(classes.toolBtn, editor.isActive("bold") ? ` ${classes.activeBtn}` : "")}
						title="Bold"
						disabled={disabled}
					>
						<BoldIcon />
					</Button>
				)}

				{extensions.italic && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("italic") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleItalic().run()}
						className={clsx(classes.toolBtn, editor.isActive("italic") ? ` ${classes.activeBtn}` : "")}
						title="Italic"
						disabled={disabled}
					>
						<ItalicIcon />
					</Button>
				)}

				{extensions.underline && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("underline") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleUnderline().run()}
						className={clsx(classes.toolBtn, editor.isActive("underline") ? ` ${classes.activeBtn}` : "")}
						title="Underline"
						disabled={disabled}
					>
						<UnderlineIcon />
					</Button>
				)}

				{extensions.strike && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("strike") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleStrike().run()}
						className={clsx(classes.toolBtn, editor.isActive("strike") ? ` ${classes.activeBtn}` : "")}
						title="Strike-through"
						disabled={disabled}
					>
						<StrikeIcon />
					</Button>
				)}

				{extensions.bulletList && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("bulletList") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleBulletList().run()}
						className={clsx(classes.toolBtn, editor.isActive("bulletList") ? ` ${classes.activeBtn}` : "")}
						title="Bullet list"
						disabled={disabled}
					>
						<BulletListIcon />
					</Button>
				)}

				{extensions.orderedList && (
					<Button
						size="sm"
						variant={`${focused && editor.isActive("orderedList") ? "" : "outline-"}secondary`}
						onClick={() => editor.chain().focus().toggleOrderedList().run()}
						className={clsx(classes.toolBtn, editor.isActive("orderedList") ? ` ${classes.activeBtn}` : "")}
						title="Numbered list"
						disabled={disabled}
					>
						<NumberedListIcon />
					</Button>
				)}

				<Button
					size="sm"
					variant="outline-secondary"
					onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
					className={classes.toolBtn}
					title="Clear formatting"
					disabled={disabled}
				>
					<ClearIcon />
				</Button>

				{/* TODO: add a button for links, think about how to allow user to set link and text */}

				{extensions.history && (
					<>
						<Button
							size="sm"
							variant="outline-secondary"
							onClick={() => editor.chain().focus().undo().run()}
							className={classes.toolBtn}
							title="Undo"
							disabled={disabled}
						>
							<UndoIcon />
						</Button>
						<Button
							size="sm"
							variant="outline-secondary"
							onClick={() => editor.chain().focus().redo().run()}
							className={classes.toolBtn}
							title="Redo"
							disabled={disabled}
						>
							<RedoIcon />
						</Button>
					</>
				)}
			</div>

			<div className={classes.btnsContainer}>
				{actions &&
					actions.length > 0 &&
					actions.map((btn, idx) =>
						React.cloneElement(btn, {
							key: `action-btn-${idx}`,
							disabled: btn.props.disabled || disabled,
							className: clsx(btn.props.className, classes.actionBtn),
						}),
					)}

				<Button
					size="sm"
					variant="outline-secondary"
					onClick={() => setExpanded(!expanded)}
					className={classes.toolBtn}
					title={expanded ? "Collapse" : "Expand"}
				>
					{expanded ? <CollapseIcon /> : <ExpandIcon />}
				</Button>
			</div>
		</div>
	);
};

Toolbar.propTypes = {
	editor: PropTypes.object,
	actions: PropTypes.arrayOf(PropTypes.element),
	expanded: PropTypes.bool,
	setExpanded: PropTypes.func,
	disableBtns: PropTypes.bool,
	headingLevel: PropTypes.number,
	color: PropTypes.string,
	setColor: PropTypes.func,
	extensions: PropTypes.object,
};

export default Toolbar;
